import type { Locale } from 'date-fns'
import { fromZonedTime } from 'date-fns-tz/fromZonedTime'
import { addHours } from 'date-fns/addHours'
import { addMinutes } from 'date-fns/addMinutes'
import { format } from 'date-fns/format'
import { isValid } from 'date-fns/isValid'
import { de } from 'date-fns/locale/de'
import { enGB } from 'date-fns/locale/en-GB'
import { it } from 'date-fns/locale/it'
import { nl } from 'date-fns/locale/nl'
import { parseISO } from 'date-fns/parseISO'
import { set } from 'date-fns/set'

const locales: { [key: string]: Locale } = {
  'de-de': de,
  'nl-nl': nl,
  'it-it': it,
}

export const localeDate = (
  date?: Date | number,
  formatting = 'EEE do MMM yyyy',
  locale = 'en-gb',
) => {
  if (!date) return ''
  const foundLocale = locales[locale] || enGB
  return format(new Date(date), formatting, { locale: foundLocale })
}

export const formatDateDDMMYY = (dateTime?: Date | number) => {
  if (!dateTime) return ''
  const date = new Date(dateTime)
  if (!isValid(date)) return ''
  const day = `${date.getDate()}`.padStart(2, '0')
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const year = date.getFullYear().toString().slice(-2)
  return `${day}/${month}/${year}`
}

// Datepicker starts on Sunday!
export const datepickerFixedDays = [7, 1, 2, 3, 4, 5, 6]

const getDate = (date: Date | string) => {
  if (typeof date === 'string') date = new Date(date)
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

const getTimeFromDate = (date: Date | string) => getDate(date).getTime()

export const getDay = (date: Date | string) => datepickerFixedDays[getDate(date).getDay()]

export const dateIsInDates = (date: Date | string, dates: Date[] | string[]) =>
  dates.some((d) => getTimeFromDate(d) === getTimeFromDate(date))

export const userStateTimeToHoursAndMins = (time = '00:00 - 00:00') => {
  const startTime = time.split(' - ')[0]
  const [hours, minutes] = startTime.split(':')
  return { hours, minutes }
}

const parseTimeComponents = (time: string) => {
  const { hours, minutes } = userStateTimeToHoursAndMins(time)
  if (!hours?.trim() || !minutes?.trim()) return null

  const hour = Number(hours)
  const minute = Number(minutes)
  return Number.isFinite(hour) && Number.isFinite(minute) ? { hour, minute } : null
}

export const userStateDateTimeToUTCString = (date: number, time = '00:00 - 00:00') => {
  const components = parseTimeComponents(time)
  if (!components) return null
  const dateTime = addMinutes(
    addHours(set(new Date(date), { hours: 0, minutes: 0 }), components.hour),
    components.minute,
  )
  // The API expects local wall-clock fields tagged as UTC, rather than an instant conversion.
  return isValid(dateTime) ? fromZonedTime(dateTime, 'UTC').toISOString() : null
}

export const getQueryDateTime = (date?: string, time?: string): Date | null => {
  if (!date) return null
  const parsedDate = parseISO(date)
  if (!isValid(parsedDate)) return null
  if (!time) return parsedDate

  const components = parseTimeComponents(time)
  if (!components) return null
  const dateTime = addMinutes(addHours(parsedDate, components.hour), components.minute)
  return isValid(dateTime) ? dateTime : null
}
